import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/common/PageHeader";
import { opportunityService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { fmtMoney } from "./OpportunitiesPage";
import { Loader2, ChevronLeft, ChevronRight } from "lucide-react";

export default function PipelinePage() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const canStage = hasPermission("OPPORTUNITY.MANAGE_STAGE");

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await opportunityService.pipeline()); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const order = data?.order || [];
  const stages = data?.stages || [];

  const move = async (oppId, stage) => {
    try { await opportunityService.moveStage(oppId, stage); toast.success(`Moved to ${stage}`); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="pipeline-page">
      <PageHeader title="Pipeline" subtitle="Visualise and advance opportunities across stages" />
      <div className="flex gap-4 overflow-x-auto pb-4" data-testid="pipeline-board">
        {stages.map((col) => {
          const idx = order.indexOf(col.stage);
          return (
            <div key={col.stage} className="min-w-[260px] w-[260px] shrink-0 bg-muted/40 rounded-xl border border-border" data-testid={`pipeline-col-${col.stage.replace(/\s/g, "-")}`}>
              <div className="p-3 border-b border-border">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sm text-foreground">{col.stage}</span>
                  <span className="text-xs text-muted-foreground">{col.count}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{fmtMoney(col.amount)} · {col.probability}%</p>
              </div>
              <div className="p-2 space-y-2 max-h-[60vh] overflow-y-auto">
                {col.items.length === 0 && <p className="text-xs text-muted-foreground text-center py-6">No deals</p>}
                {col.items.map((o) => (
                  <div key={o.id} className="bg-card rounded-lg border border-border p-3 shadow-sm" data-testid={`pipeline-card-${o.id}`}>
                    <button onClick={() => navigate(`/admin/opportunities/${o.id}`)} className="text-left w-full">
                      <p className="text-sm font-medium text-foreground hover:text-primary">{o.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{o.customerName || "—"}</p>
                      <p className="text-xs font-semibold text-foreground mt-1">{fmtMoney(o.amount)}</p>
                    </button>
                    {canStage && (
                      <div className="flex justify-between mt-2 pt-2 border-t border-border">
                        <button disabled={idx <= 0} onClick={() => move(o.id, order[idx - 1])} className="text-muted-foreground hover:text-primary disabled:opacity-30" data-testid={`pipeline-prev-${o.id}`}><ChevronLeft className="h-4 w-4" /></button>
                        <button disabled={idx >= order.length - 1} onClick={() => move(o.id, order[idx + 1])} className="text-muted-foreground hover:text-primary disabled:opacity-30" data-testid={`pipeline-next-${o.id}`}><ChevronRight className="h-4 w-4" /></button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
